// 주입 가능한 시계 — 이 저장소에서 시간을 실제로 읽는 **유일한** 자리
//
// eslint.config.js 의 시간 전역 함수 금지 규칙이 예외로 빼 둔 파일이 이것 하나다.
// 다른 모든 코드는 현재 시각을 직접 읽지 않고 이 모듈이 주는 시계를 **받아** 쓴다.
// 그래야 테스트가 시각을 정할 수 있고, 클린 체크아웃에서 같은 입력이 같은 결과를 낸다.
//
// 시계에 축이 둘인 이유를 적어 둔다. 벽시계(now)는 UTC epoch 기준 절대 시각이라 사람에게
// 보여 줄 수 있고 서버와 맞출 수 있지만, 사용자가 시각을 손으로 고치거나 OS 가 NTP 로
// 점프시키면 뒤로 갈 수 있다. 단조 시계(elapsed)는 뒤로 가지 않아 경과를 재기에 맞지만
// **기기 절전 중에는 멈춘다.** 화면 꺼짐 방지를 못 쓰는 것이 이 제품의 확정 사실이라
// 화면 꺼짐은 예외가 아니라 상시 조건이고, 그래서 두 축을 둘 다 들고 다니며 서로
// 교차 검사한다 — 둘의 흐른 양이 어긋나면 그동안 기기가 잤다는 뜻이고, 그때는 시계
// 동기를 강제로 다시 수렴시킨다.
//
// 이 파일은 계산 코어에 있으므로 프레임워크와 저장소를 import 하지 않는다.

import { duration, type Duration, instant, type Instant, toMillis } from './brand.ts'

export interface Clock {
  /** 벽시계 — UTC epoch 기준 절대 시각. 정수 밀리초. 뒤로 갈 수 있다. */
  now(): Instant
  /** 단조 시계 — 어떤 기준점부터의 경과. 정수 밀리초. 뒤로 가지 않지만 절전 중 멈춘다. */
  elapsed(): Duration
}

/**
 * 실제 시계. 프로세스 전체에서 이것 하나만 만들어 주입한다.
 *
 * 단조 축의 기준점을 만드는 시점에 고정하는 이유는, 여러 시계 인스턴스가 각자 다른
 * 기준점을 갖게 두면 두 모듈이 주고받은 경과값이 서로 다른 원점 위의 수가 되어
 * 조용히 틀리기 때문이다.
 */
export function systemClock(): Clock {
  // performance.now() 는 소수점이 붙은 밀리초다. 이 제품의 시간 규약이 정수 밀리초라
  // 여기서 한 번만 바닥으로 내려 브랜드 타입에 넣는다. 바닥으로 내리면 단조성이
  // 유지된다 — 반올림은 값이 커졌다 작아지는 순간을 만들지 않지만, 굳이 두 규칙을
  // 섞을 이유가 없다.
  const origin = performance.now()
  return {
    now: () => instant(Date.now()),
    elapsed: () => duration(Math.floor(performance.now() - origin)),
  }
}

export interface TestClock extends Clock {
  /** 벽시계만 갈아 끼운다. 단조 시계는 건드리지 않는다 — NTP 점프와 사용자 시각 변경. */
  set(at: Instant): void
  /** 벽시계와 단조 시계를 함께 민다. 평범하게 시간이 흐른 것. */
  advance(by: Duration): void
  /** 절전을 재현한다 — 벽시계만 흐르고 단조 시계는 멈춘다. */
  sleep(by: Duration): void
}

/**
 * 시험용 고정 시계. 테스트가 시각을 정한다.
 *
 * `advance` 와 `sleep` 을 가른 것이 이 시계의 존재 이유다. 두 축을 함께만 밀 수 있는
 * 시계로는 "기기가 자는 동안 벽시계만 흘렀다" 는 상태를 만들 수 없고, 그러면 단조 시계
 * 불연속 감지에 테스트를 붙일 방법이 없다.
 */
export function testClock(startAt: Instant, startElapsed: Duration = duration(0)): TestClock {
  let wall = toMillis(startAt)
  let mono = toMillis(startElapsed)

  const forward = (by: Duration, what: string): number => {
    const millis = toMillis(by)
    if (millis < 0) {
      throw new RangeError(`${what}: 시계를 뒤로 밀 수 없다 (${String(millis)}ms)`)
    }
    return millis
  }

  return {
    now: () => instant(wall),
    elapsed: () => duration(mono),
    set: (at: Instant) => {
      wall = toMillis(at)
    },
    advance: (by: Duration) => {
      const millis = forward(by, 'advance')
      wall += millis
      mono += millis
    },
    sleep: (by: Duration) => {
      wall += forward(by, 'sleep')
    },
  }
}
